import React, { useState, useEffect } from 'react';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { Camera, Users, User, Upload, X } from 'lucide-react';
import { baseUrl } from '../config';



// Modern UI components
const Card = ({ children, className = '' }) => (
    <div className={`bg-white shadow-lg rounded-xl p-6 ${className}`}>{children}</div>
);

const CardHeader = ({ children }) => <div className="mb-4">{children}</div>;
const CardTitle = ({ children }) => <h2 className="text-2xl font-bold text-gray-800">{children}</h2>;
const CardContent = ({ children }) => <div>{children}</div>;

const Input = ({ placeholder, value, onChange, onKeyPress, type = "text", className = '' }) => (
    <input
        className={`border-2 border-gray-300 rounded-lg px-4 py-2 w-full focus:outline-none focus:border-blue-500 transition duration-300 ${className}`}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
    />
);

const Button = ({ onClick, children, className = '', disabled = false }) => (
    <button
        className={`bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition duration-300 ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={onClick}
        disabled={disabled}
    >
        {children}
    </button>
);

const Table = ({ children }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 shadow-sm rounded-lg overflow-hidden">
            {children}
        </table>
    </div>
);
const TableHeader = ({ children }) => <thead className="bg-gray-50">{children}</thead>;
const TableBody = ({ children }) => <tbody className="bg-white divide-y divide-gray-200">{children}</tbody>;
const TableRow = ({ children }) => <tr>{children}</tr>;
const TableHead = ({ children }) => (
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {children}
    </th>
);
const TableCell = ({ children }) => <td className="px-6 py-4 whitespace-nowrap">{children}</td>;

const Tabs = ({ children, defaultValue }) => {
    const [activeTab, setActiveTab] = useState(defaultValue);
    return (
        <div className="tabs">
            {React.Children.map(children, child =>
                React.cloneElement(child, { activeTab, setActiveTab })
            )}
        </div>
    );
};

const TabsList = ({ children }) => <div className="flex space-x-2 mb-6">{children}</div>;

const TabsTrigger = ({ value, activeTab, setActiveTab, children }) => (
    <button
        className={`px-4 py-2 rounded-lg transition duration-300 ${activeTab === value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        onClick={() => setActiveTab(value)}
    >
        {children}
    </button>
);

const TabsContent = ({ value, activeTab, children }) => (
    activeTab === value ? <div>{children}</div> : null
);

const initialTeamMembers = [
    { id: 1, name: 'John Doe', role: 'Lightman', task: '', status: 'Available', notes: '', isAssigned: false },
    { id: 2, name: 'Jane Smith', role: 'Hair Stylist', task: '', status: 'On Set', notes: '', isAssigned: false },
    { id: 3, name: 'Mike Johnson', role: 'Lead Actor', task: '', status: 'In Makeup', notes: '', isAssigned: false },
    { id: 4, name: 'Emily Brown', role: 'Villain', task: '', status: 'Rehearsing', notes: '', isAssigned: false },
];

const ScriptUpload = ({ memberId, onUpload }) => {
    const [file, setFile] = useState(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = () => {
        if (file) {
            onUpload(memberId, file.name);
            setFile(null);
        }
    };

    return (
        <div className="flex items-center space-x-2">
            <Input
                type="file"
                onChange={handleFileChange}
                className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <Button onClick={handleUpload} className="bg-green-500 hover:bg-green-600">
                <Upload size={16} className="mr-2" />
                Upload
            </Button>
        </div>
    );
};

const TeamMemberList = ({ members, onAssignTask, onUpdateStatus, onUploadScript, onUpdateNotes, onToggleTaskAssignment }) => {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Current Task</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Script</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Task Assignment</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {members.map((member) => (
                    <TableRow key={member.id}>
                        <TableCell>{member.name}</TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell>{member.task || 'No task assigned'}</TableCell>
                        <TableCell>
                            <select
                                className="border rounded px-2 py-1"
                                value={member.status}
                                onChange={(e) => onUpdateStatus(member.id, e.target.value)}
                            >
                                <option>Available</option>
                                <option>On Set</option>
                                <option>In Makeup</option>
                                <option>Rehearsing</option>
                                <option>On Break</option>
                            </select>
                        </TableCell>
                        <TableCell>
                            <Input
                                placeholder="Enter task"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        onAssignTask(member.id, e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                            />
                        </TableCell>
                        <TableCell>
                            {['Lead Actor', 'Villain', 'Actor'].includes(member.role) && (
                                <ScriptUpload memberId={member.id} onUpload={onUploadScript} />
                            )}
                            {member.script && <p className="text-sm text-gray-600">Current: {member.script}</p>}
                        </TableCell>
                        <TableCell>
                            <textarea
                                value={member.notes}
                                onChange={(e) => onUpdateNotes(member.id, e.target.value)}
                                className="border rounded px-2 py-1 w-full h-24"
                            />
                        </TableCell>
                        <TableCell>
                            <Button
                                onClick={() => onToggleTaskAssignment(member.id)}
                                className={`w-full ${member.isAssigned ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
                            >
                                {member.isAssigned ? <X size={16} className="mr-2" /> : <User size={16} className="mr-2" />}
                                {member.isAssigned ? 'Unassign' : 'Assign'}
                            </Button>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const Dashboard = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [newMember, setNewMember] = useState({ name: '', role: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchTeamMembers = async () => {
            setIsLoading(true);
            try {
                const response = await new Promise(resolve => 
                    setTimeout(() => resolve({ data: initialTeamMembers }), 1000)
                );
                setTeamMembers(response.data);
            } catch (err) {
                setError('Failed to fetch team members. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTeamMembers();
    }, []);

    const saveTeamMembersToServer = async (updatedMembers) => {
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
        } catch (err) {
            setError('Failed to save changes. Please try again.');
        }
    };

    const handleAssignTask = (id, task) => {
        const updatedMembers = teamMembers.map((member) =>
            member.id === id ? { ...member, task, isAssigned: true } : member
        );
        setTeamMembers(updatedMembers);
        saveTeamMembersToServer(updatedMembers);
    };

    const handleUpdateStatus = (id, status) => {
        const updatedMembers = teamMembers.map((member) =>
            member.id === id ? { ...member, status } : member
        );
        setTeamMembers(updatedMembers);
        saveTeamMembersToServer(updatedMembers);
    };

    const handleAddMember = () => {
        if (newMember.name && newMember.role) {
            const updatedMembers = [
                ...teamMembers,
                { ...newMember, id: teamMembers.length + 1, task: '', status: 'Available', script: '', notes: '', isAssigned: false },
            ];
            setTeamMembers(updatedMembers);
            saveTeamMembersToServer(updatedMembers);
            setNewMember({ name: '', role: '' });
        }
    };

    const handleUploadScript = (id, scriptName) => {
        const updatedMembers = teamMembers.map((member) =>
            member.id === id ? { ...member, script: scriptName } : member
        );
        setTeamMembers(updatedMembers);
        saveTeamMembersToServer(updatedMembers);
    };

    const handleUpdateNotes = (id, notes) => {
        const updatedMembers = teamMembers.map((member) =>
            member.id === id ? { ...member, notes } : member
        );
        setTeamMembers(updatedMembers);
        saveTeamMembersToServer(updatedMembers);
    };

    const handleToggleTaskAssignment = (id) => {
        const updatedMembers = teamMembers.map((member) =>
            member.id === id ? { ...member, isAssigned: !member.isAssigned, task: !member.isAssigned ? member.task : '' } : member
        );
        setTeamMembers(updatedMembers);
        saveTeamMembersToServer(updatedMembers);
    };

    const filteredMembers = teamMembers.filter(
        (member) =>
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.role.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const crewMembers = filteredMembers.filter(
        (member) => !['Lead Actor', 'Villain', 'Actor'].includes(member.role)
    );
    const castMembers = filteredMembers.filter((member) =>
        ['Lead Actor', 'Villain', 'Actor'].includes(member.role)
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">
            <SEO
              title="Cast Management"
              description="Manage your film's cast and crew assignments with ProducerBuddy's cast management tool — roles, schedules, and contact details all in one place."
              url="https://producerbuddy.pilomation.com/cast-management"
              robots="noindex, nofollow"
            />
            <SubNav />
            <div className="p-8">
                {/* <h1 className="text-5xl font-extrabold mb-8 text-gray-800 text-center">
                    Film Production Team Management
                </h1> */}
                {isLoading && (
                    <Card className="mb-8">
                        <CardContent>
                            <p className="text-center">Loading team members...</p>
                        </CardContent>
                    </Card>
                )}
                {error && (
                    <Card className="mb-8 bg-red-100">
                        <CardContent>
                            <p className="text-red-500 text-center">{error}</p>
                        </CardContent>
                    </Card>
                )}
                <Card className="mb-8 transform hover:scale-105 transition-transform duration-300">
                    <CardHeader>
                        <CardTitle>Add New Team Member</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-4">
                        <Input
                            placeholder="Name"
                            value={newMember.name}
                            onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                            className="flex-grow"
                        />
                        <Input
                            placeholder="Role"
                            value={newMember.role}
                            onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                            className="flex-grow"
                        />
                        <Button 
                            onClick={handleAddMember} 
                            className="w-full sm:w-auto"
                            disabled={!newMember.name || !newMember.role}
                        >
                            <User size={16} className="mr-2" />
                            Add Member
                        </Button>
                    </CardContent>
                </Card>
                <Card className="mb-8">
                    <CardContent>
                        <Input
                            placeholder="Search members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full"
                        />
                    </CardContent>
                </Card>
                <Tabs defaultValue="all" className="mb-8">
                    <TabsList className="bg-white p-2 rounded-lg shadow-md">
                        <TabsTrigger value="all" className="text-lg">
                            <User size={16} className="mr-2" />
                            All Members
                        </TabsTrigger>
                        <TabsTrigger value="crew" className="text-lg">
                            <Camera size={16} className="mr-2" />
                            Crew
                        </TabsTrigger>
                        <TabsTrigger value="cast" className="text-lg">
                            <Users size={16} className="mr-2" />
                            Cast
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="all">
                        <Card>
                            <CardHeader>
                                <CardTitle>All Team Members</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <TeamMemberList
                                    members={filteredMembers}
                                    onAssignTask={handleAssignTask}
                                    onUpdateStatus={handleUpdateStatus}
                                    onUploadScript={handleUploadScript}
                                    onUpdateNotes={handleUpdateNotes}
                                    onToggleTaskAssignment={handleToggleTaskAssignment}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                    <TabsContent value="crew">
                        <Card>
                            <CardHeader>
                                <CardTitle>Crew Members</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <TeamMemberList
                                    members={crewMembers}
                                    onAssignTask={handleAssignTask}
                                    onUpdateStatus={handleUpdateStatus}
                                    onUploadScript={handleUploadScript}
                                    onUpdateNotes={handleUpdateNotes}
                                    onToggleTaskAssignment={handleToggleTaskAssignment}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                    <TabsContent value="cast">
                        <Card>
                            <CardHeader>
                                <CardTitle>Cast Members</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <TeamMemberList
                                    members={castMembers}
                                    onAssignTask={handleAssignTask}
                                    onUpdateStatus={handleUpdateStatus}
                                    onUploadScript={handleUploadScript}
                                    onUpdateNotes={handleUpdateNotes}
                                    onToggleTaskAssignment={handleToggleTaskAssignment}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default Dashboard;
